<script setup lang="ts">
import type { TableColumn } from "@nuxt/ui";

import {
	computed, h, ref, resolveComponent, watch,
} from "vue";

import { watchDebounced } from "@vueuse/core";

import { formatToMinute } from "@ca/formatters/dates";

import { client } from "@/api";
import { useAgentMemories } from "@/queries/agent";
const { data } = useAgentMemories();

const search = ref("");
const searchResults = ref<MemoryRow[]>([]);
const searching = ref(false);
const UBadge = resolveComponent("UBadge");
const UButton = resolveComponent("UButton");
const memories = computed(() => data.value ?? []);
const displayedMemories = computed<MemoryRow[]>(() => {
	if (search.value.length === 0) {
		return memories.value as MemoryRow[];
	}
	return searchResults.value;
});
type Memory = typeof memories.value[number];
type MemoryRow = Memory & { cosineDistance?: number };
type MemorySource = Memory["sources"][number];

const memoryTypeColors = {
	counterexample: "error",
	example: "success",
	heuristic: "info",
	idea: "warning",
	other: "neutral",
	rubric: "primary",
};
const isSubmissionSource = (source: MemorySource) => source.sourceType === "submission";

watchDebounced(search, async () => {
	if (search.value.trim().length === 0) {
		searchResults.value = [];
		return;
	}
	searching.value = true;
	const result = await client.api.v1.agents.memories._search.get({
		query: {
			query: search.value,
		},
	});
	if (result.status === 200 && result.data?.memories) {
		searchResults.value = result.data.memories as MemoryRow[];
	} else {
		searchResults.value = [];
	}
	searching.value = false;
}, {
	debounce: 500,
});
watch(search, () => {
	searching.value = search.value.trim().length > 0;
});
const columns: TableColumn<MemoryRow>[] = [
	{
		header: "Created At",
		accessorKey: "createdAt",
		cell: ({ row }) => formatToMinute(row.original.createdAt),
	},
	{
		header: "Statement",
		accessorKey: "statement",
		cell: ({ row }) => h("div", { class: "max-w-xl text-wrap flex flex-col items-start" }, [
			h("span", { class: "text-sm" }, row.original.statement),
			row.original.cosineDistance !== undefined
				? h(UBadge, {
					variant: "subtle",
					color: "neutral",
					class: "mt-1",
				}, () => `Cosine Distance from '${search.value}': ${row.original.cosineDistance?.toFixed(4)}`)
				: null,
		]),
	},
	{
		header: "Type",
		accessorKey: "type",
		cell: ({ row }) =>
			h("div", { class: "flex gap-2 items-center" }, [
				h(UBadge, {
					variant: "subtle", class: "capitalize", color: "neutral", 
				}, () => row.original.type.replaceAll("_", " ")),
				h(UBadge, {
					variant: "subtle", class: "capitalize", color: memoryTypeColors[row.original.kind], 
				}, () => row.original.kind),
			]),
	},
	{
		header: "Source",
		accessorKey: "sources",
		cell: ({ row }) => {
			const sources = (row.original.sources ?? []).filter(isSubmissionSource);
			if (sources.length === 0) {
				return "--";
			}
			return h("div", { class: "flex flex-wrap items-center gap-2" }, sources.map((source) => {
				return h("div", { class: "flex items-center gap-1" }, [
				
					h(UButton, {
						icon: "ic-round-open-in-new",
						variant: "link",
						size: "xs",
						to: `/submissions/${source.sourceId}`,
						label: "Submission",
					}),
				]);
			}));
		},
	},
];
</script>   

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-center">
      <UInput
        v-model="search"
        placeholder="Query memories"
        icon="ic-round-search"
        size="xl"
        class="w-80"
      />
    </div>
    <UTable
      :data="displayedMemories"
      :columns="columns"
      :loading="searching"
    />
  </div>
</template>